import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  Alert,
  Skeleton,
  Button,
  Stack,
  Drawer,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Chat as ChatIcon,
  Refresh as RefreshIcon,
  AccessTime as AccessTimeIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useStatistics } from '../../hooks/useStatistics';
import { UsageChart, HourlyUsageChart, ExpertRankingTable } from '../Charts';
import { AdminHeader } from './AdminHeader';
import axios from 'axios';
import { UserManagementData } from '../../types/admin';
import UserChatHistory from './UserChatHistory';

export const UsageStatistics: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    usageStats,
    analyticsData,
    loading,
    error,
    fetchUsageStatistics,
    fetchAnalyticsData,
    clearError,
  } = useStatistics();

  const [period, setPeriod] = useState<'7d' | '30d' | '90d'>('30d');

  // ユーザー検索用ステート
  const [userSearch, setUserSearch] = useState('');
  const [userOptions, setUserOptions] = useState<UserManagementData[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserManagementData | null>(null);

  // Drawer open state
  const [chatOpen, setChatOpen] = useState(false);

  // ユーザー検索（簡易デバウンス 300ms）
  useEffect(() => {
    const handler = setTimeout(async () => {
      if (userSearch.trim() === '') {
        setUserOptions([]);
        return;
      }
      try {
        const resp = await axios.get(`/api/admin/users?search=${encodeURIComponent(userSearch)}&limit=10`);
        setUserOptions(resp.data.data);
      } catch (err) {
        console.error('Failed to search users', err);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [userSearch]);

  useEffect(() => {
    fetchUsageStatistics(period, selectedUser?.id);
    fetchAnalyticsData(period, selectedUser?.id);
  }, [fetchUsageStatistics, fetchAnalyticsData, period, selectedUser]);

  const handleRefresh = () => {
    fetchUsageStatistics(period, selectedUser?.id);
    fetchAnalyticsData(period, selectedUser?.id);
  };

  // Calculate summary statistics
  const totalChats = usageStats.reduce((sum, stat) => sum + stat.chatCount, 0);
  const avgDailyChats = usageStats.length > 0 ? Math.round(totalChats / usageStats.length) : 0;

  // Analytics data for new features
  const totalUsers = analyticsData?.totalUsers || 0;
  const totalSessions = analyticsData?.totalChatSessions || 0;
  const hourlyPattern = analyticsData?.hourlyUsagePattern || [];
  const popularExperts = analyticsData?.mostPopularExperts || [];

  // Prepare chart data
  const usageChartData = usageStats.map(stat => ({
    date: stat.date,
    value: stat.chatCount,
    label: `${stat.date}: ${stat.chatCount}件`,
  }));

  return (
    <Box>
      <AdminHeader
        title="利用統計・分析"
        description="システムの利用状況とAI応答の分析データを確認します"
        pageType="statistics"
      />

      {/* コントロール部分 - レスポンシブ対応 */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, mt: 2 }}>
        <Grid container spacing={2} alignItems="end">
          {/* ユーザー検索 */}
          <Grid item xs={12} md={5}>
            <Autocomplete
              fullWidth
              options={userOptions}
              getOptionLabel={(option) => `${option.fullName} (${option.email})`}
              onInputChange={(_, value) => setUserSearch(value)}
              onChange={(_, value) => setSelectedUser(value)}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="ユーザー検索" 
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                    },
                  }}
                />
              )}
              value={selectedUser}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              size={isMobile ? 'small' : 'medium'}
            />
          </Grid>

          {/* チャット履歴ボタン */}
          <Grid item xs={12} sm={6} md={2}>
            <Tooltip title={!selectedUser ? "ユーザーを選択してください" : ""}>
              <span>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ChatIcon />}
                  disabled={!selectedUser}
                  onClick={() => setChatOpen(true)}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    whiteSpace: 'nowrap',
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  }}
                >
                  チャット履歴
                </Button>
              </span>
            </Tooltip>
          </Grid>

          {/* 期間選択 */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
              <InputLabel>期間</InputLabel>
              <Select
                value={period}
                onChange={(e) => setPeriod(e.target.value as '7d' | '30d' | '90d')}
                label="期間"
              >
                <MenuItem value="7d">過去7日間</MenuItem>
                <MenuItem value="30d">過去30日間</MenuItem>
                <MenuItem value="90d">過去90日間</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* 更新ボタン */}
          <Grid item xs={12} sm={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleRefresh}
              startIcon={<RefreshIcon />}
              disabled={loading}
              size={isMobile ? 'small' : 'medium'}
              sx={{
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
              }}
            >
              更新
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" onClose={clearError} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* サマリーカード */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PeopleIcon sx={{ color: 'primary.main', mr: 1, fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    fontWeight: 600,
                  }}
                >
                  総ユーザー数
                </Typography>
              </Box>
              {loading ? (
                <Skeleton variant="text" width={60} height={40} />
              ) : (
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: 'primary.main',
                    fontSize: { xs: '1.8rem', sm: '2.5rem' },
                  }}
                >
                  {totalUsers}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ChatIcon sx={{ color: 'secondary.main', mr: 1, fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    fontWeight: 600,
                  }}
                >
                  総スレッド数
                </Typography>
              </Box>
              {loading ? (
                <Skeleton variant="text" width={60} height={40} />
              ) : (
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: 'secondary.main',
                    fontSize: { xs: '1.8rem', sm: '2.5rem' },
                  }}
                >
                  {totalSessions}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ChatIcon sx={{ color: 'success.main', mr: 1, fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontSize: { xs: '0.9rem', sm: '1.25rem' },
                    fontWeight: 600,
                    lineHeight: 1.2,
                  }}
                >
                  AIへのメッセージ送信数
                </Typography>
              </Box>
              {loading ? (
                <Skeleton variant="text" width={60} height={40} />
              ) : (
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: 'success.main',
                    fontSize: { xs: '1.8rem', sm: '2.5rem' },
                  }}
                >
                  {analyticsData?.totalUserMessages || 0}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* チャート - レスポンシブ対応 */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          {loading ? (
            <Skeleton variant="rectangular" height={isMobile ? 280 : 360} />
          ) : (
            <UsageChart
              data={usageChartData}
              title="チャット利用状況の推移"
              height={isMobile ? 250 : 300}
              color="#2E5BBA"
            />
          )}
        </Grid>
      </Grid>

      {/* 時間帯別利用チャート */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          {loading ? (
            <Skeleton variant="rectangular" height={isMobile ? 320 : 400} />
          ) : (
            <HourlyUsageChart
              data={hourlyPattern}
              title="時間帯別利用パターン"
              height={isMobile ? 250 : 300}
            />
          )}
        </Grid>
      </Grid>

      {/* AI専門家ランキング */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          {loading ? (
            <Skeleton variant="rectangular" height={isMobile ? 320 : 400} />
          ) : (
            <ExpertRankingTable
              data={popularExperts}
              title="AI専門家人気ランキング"
              maxRows={isMobile ? 5 : 10}
            />
          )}
        </Grid>
      </Grid>

      {/* 詳細情報 - レスポンシブ対応 */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 3 }}>
        <Typography 
          variant="h6" 
          gutterBottom
          sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
        >
          期間別詳細データ
        </Typography>
        <Box sx={{ maxHeight: { xs: 300, sm: 400 }, overflow: 'auto' }}>
          {loading ? (
            Array.from({ length: 5 }).map((_, index) => (
              <Skeleton key={index} variant="text" height={40} sx={{ mb: 1 }} />
            ))
          ) : usageStats.length > 0 ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography 
                  variant="subtitle2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                  }}
                >
                  日付
                </Typography>
                {usageStats.map((stat, index) => (
                  <Typography 
                    key={index} 
                    variant="body2" 
                    sx={{ 
                      mb: 1,
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    }}
                  >
                    {new Date(stat.date).toLocaleDateString('ja-JP', {
                      month: isMobile ? 'short' : 'long',
                      day: 'numeric',
                    })}
                  </Typography>
                ))}
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography 
                  variant="subtitle2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                  }}
                >
                  チャット数
                </Typography>
                {usageStats.map((stat, index) => (
                  <Typography 
                    key={index} 
                    variant="body2" 
                    sx={{ 
                      mb: 1,
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    }}
                  >
                    {stat.chatCount}件
                  </Typography>
                ))}
              </Grid>
            </Grid>
          ) : (
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ textAlign: 'center', py: 4 }}
            >
              表示するデータがありません
            </Typography>
          )}
        </Box>
      </Paper>

      {/* User Chat History Drawer */}
      <Drawer
        anchor="right"
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        variant={isMobile ? 'temporary' : 'persistent'}
        sx={{ 
          '& .MuiDrawer-paper': { 
            width: isMobile ? '100%' : 480,
            maxWidth: '100vw',
          } 
        }}
      >
        {selectedUser && (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ 
              p: { xs: 1.5, sm: 2 }, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              borderBottom: 1,
              borderColor: 'divider',
            }}>
              <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                {selectedUser.fullName}のチャット履歴
              </Typography>
              <IconButton 
                onClick={() => setChatOpen(false)}
                size={isMobile ? 'small' : 'medium'}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            {/* Content */}
            <Box sx={{ flexGrow: 1, overflow: 'auto', p: { xs: 1, sm: 2 } }}>
              <UserChatHistory
                userId={selectedUser.id}
                userName={selectedUser.fullName}
              />
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};